import React, { useMemo } from "react";
import { DateTime } from "luxon";
import { footprintDeEvento, relacionEventoEspacio } from "../../utils/espacios";
import { diasDeEvento } from "./dias";

const ACCENT = "#1b4d2e";

// Vista de mes con UN CARRIL POR ESPACIO dentro de cada día. El grid de mes
// clásico no sirve en un venue multiespacio: mezcla los eventos de todos los
// salones en una sola pila y no se puede leer qué está libre. Aquí cada día
// muestra siempre todos los espacios visibles, en el MISMO orden que la
// leyenda — un carril vacío es la información importante (ese salón se puede
// vender ese día).
//
// Un carril distingue DOS cosas que no son lo mismo (ver
// relacionEventoEspacio):
//   - el evento ESTÁ ahí        → chip sólido con el nombre
//   - el espacio quedó sin vender POR ese evento (contención parcial o
//     incompatibilidad) → barra rayada, sin nombre
// Pintarlas igual hacía que un evento en Salón C + Jardín A se leyera como si
// ocurriera en los cuatro espacios. Los eventos legacy sin footprint sí salen
// como directos en todos: ocupan el venue entero, y ese muro es justo la
// señal de que nunca se les asignó espacio.
//
// Se renderiza en lugar de FullCalendar (no con él): FullCalendar sin el
// plugin premium de recursos no sabe de carriles, y pelearse con su DOM
// interno sale más caro que dibujar la retícula, que además ya es el patrón
// de la casa (ver YearView y el calendario de leads).

// Semana lunes-first, igual que el locale `es` de FullCalendar que se ve hoy.
const DOW = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

// "Salón A" → "SA", "Jardín A" → "JA". Dos iniciales bastan para distinguir
// los espacios de un venue real y caben en la canaleta del carril.
export const abreviarEspacio = (nombre = "") =>
  nombre
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase() || "?";

export default function MonthSpacesView({
  year,
  month, // 0-11
  espacios = [],
  events = [],
  espacioColor = {},
  timeZone = "America/Mexico_City",
  onEventClick,
  onDateClick,
  soloLectura = false,
}) {
  // Índice día → espacioId → eventos que lo bloquean. Se arma una sola vez
  // por mes en lugar de recorrer los eventos dentro de cada celda.
  const porDia = useMemo(() => {
    const mapa = new Map();
    events.forEach((ev) => {
      const fp = footprintDeEvento(ev);
      const ids = ev.extendedProps?.espaciosIds || [];
      const toca = espacios
        .map((esp) => ({ esp, rel: relacionEventoEspacio(ids, fp, esp) }))
        .filter((x) => x.rel);
      if (toca.length === 0) return;
      diasDeEvento(ev, timeZone).forEach((dia) => {
        if (!mapa.has(dia)) mapa.set(dia, new Map());
        const delDia = mapa.get(dia);
        toca.forEach(({ esp, rel }) => {
          if (!delDia.has(esp.id)) delDia.set(esp.id, []);
          delDia.get(esp.id).push({ ev, rel });
        });
      });
    });
    return mapa;
  }, [events, espacios, timeZone]);

  // Celdas del mes, lunes-first, incluyendo el relleno de los meses vecinos
  // para que la retícula quede cuadrada (como la vista de mes de siempre).
  const celdas = useMemo(() => {
    const primero = DateTime.fromObject(
      { year, month: month + 1, day: 1 },
      { zone: timeZone }
    );
    const inicio = primero.minus({ days: (primero.weekday + 6) % 7 });
    const total = Math.ceil(((primero.daysInMonth + ((primero.weekday + 6) % 7)) / 7)) * 7;
    return Array.from({ length: total }, (_, i) => {
      const d = inicio.plus({ days: i });
      return {
        iso: d.toISODate(),
        dia: d.day,
        delMes: d.month === month + 1,
        esHoy: d.toISODate() === DateTime.now().setZone(timeZone).toISODate(),
      };
    });
  }, [year, month, timeZone]);

  if (espacios.length === 0) return null;

  return (
    // Con muchos espacios las celdas crecen a lo alto y los días se angostan:
    // el scroll horizontal con ancho mínimo evita que los carriles se
    // aplasten hasta ser ilegibles en pantallas chicas.
    <div className="overflow-x-auto">
      <div className="min-w-[860px]">
        <div className="grid grid-cols-7 border-b border-zinc-200 dark:border-zinc-700/60">
          {DOW.map((d) => (
            <div
              key={d}
              className="px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400"
            >
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {celdas.map((celda) => {
            const delDia = porDia.get(celda.iso);
            return (
              <div
                key={celda.iso}
                onClick={
                  soloLectura || !onDateClick ? undefined : () => onDateClick(celda.iso)
                }
                className={`min-h-[104px] border-b border-r border-zinc-200 p-1.5 dark:border-zinc-700/60 ${
                  celda.delMes
                    ? "bg-white dark:bg-zinc-800/50"
                    : "bg-zinc-50/70 dark:bg-zinc-900/40"
                } ${soloLectura ? "" : "cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-700/30"}`}
              >
                <div className="mb-1 flex justify-end">
                  <span
                    className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs ${
                      celda.esHoy
                        ? "font-bold text-white"
                        : celda.delMes
                          ? "font-medium text-zinc-700 dark:text-zinc-300"
                          : "text-zinc-400 dark:text-zinc-600"
                    }`}
                    style={celda.esHoy ? { backgroundColor: ACCENT } : undefined}
                  >
                    {celda.dia}
                  </span>
                </div>

                <div className="space-y-0.5">
                  {espacios.map((esp) => {
                    const entradas = delDia?.get(esp.id) || [];
                    // El nombre solo se imprime donde el evento realmente
                    // está; lo demás es "no se puede vender por culpa de".
                    const directos = entradas.filter((x) => x.rel === "directo");
                    const indirectos = entradas.filter((x) => x.rel === "consecuencia");
                    const evs = directos;
                    const color = espacioColor[esp.id] || ACCENT;
                    return (
                      <div
                        key={esp.id}
                        className="flex items-center gap-1"
                        title={esp.nombre}
                      >
                        <span
                          className="w-4 shrink-0 text-[9px] font-bold leading-none"
                          style={{ color: entradas.length ? color : undefined }}
                        >
                          <span className={entradas.length ? "" : "text-zinc-300 dark:text-zinc-600"}>
                            {abreviarEspacio(esp.nombre)}
                          </span>
                        </span>
                        {evs.length === 0 ? (
                          indirectos.length > 0 ? (
                            // Bloqueado por otro salón: rayado, sin nombre.
                            // El evento NO ocurre aquí.
                            <span
                              className="h-3 flex-1 rounded-sm opacity-60"
                              title={`No disponible por ${indirectos
                                .map((x) => x.ev.title)
                                .join(", ")}`}
                              style={{
                                backgroundImage: `repeating-linear-gradient(45deg, ${color} 0 2px, transparent 2px 5px)`,
                              }}
                            />
                          ) : (
                            // Carril libre: una línea tenue. Es la información
                            // que el dueño está buscando, no un hueco.
                            <span className="h-3 flex-1 rounded-sm border-b border-dashed border-zinc-200 dark:border-zinc-700" />
                          )
                        ) : (
                          <span className="flex min-w-0 flex-1 items-center gap-0.5">
                            {evs.slice(0, 2).map(({ ev }) => (
                              <button
                                key={ev.id}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEventClick?.(ev);
                                }}
                                title={ev.title}
                                className="min-w-0 flex-1 truncate rounded-sm px-1 py-px text-left text-[10px] font-medium leading-tight text-white transition hover:opacity-90"
                                style={{ backgroundColor: ev.color || color }}
                              >
                                {ev.title}
                              </button>
                            ))}
                            {evs.length > 2 && (
                              <span className="shrink-0 text-[9px] font-semibold text-zinc-400">
                                +{evs.length - 2}
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
